import clsx from 'clsx'
import { useRef } from 'react'

import mainStyles from 'assets/css/main.module.scss'
import { ReactComponent as ArrowIcon } from 'assets/icons/arrow_right.svg'
import { ReactComponent as ChevronRight } from 'assets/icons/chevron-right.svg'
import { ReactComponent as DocumentIcon } from 'assets/icons/document.svg'
import { ReactComponent as FolderIcon } from 'assets/icons/folder.svg'
import { ReactComponent as VideoIcon } from 'assets/icons/video-rounded.svg'
import BottomArc from 'components/bottomArc/BottomArc'
import GridCardLayout from 'components/cardGridLayout/CardGridLayout'
import { Button } from 'components/common/button/Button'
import { FrameworkWheelPage } from 'components/interactiveNavigation/FrameworkWheel'
import KeyTasks from 'components/keyTasks/KeyTasks'
import NavBar from 'components/navBar/NavBar'
import { ReactComponent as LinkIcon } from 'components/nmaModal/icons/link.svg'
import PageBanner from 'components/pageBanner/PageBanner'
import { ReturnToLandingButton } from 'components/returnToLandingButton/ReturnToLandingButton'
import { TopReturnToLandingButton } from 'components/topReturnToLandingButton/TopReturnToLandingButton'
import useHardNavigate from 'hooks/useHardNavigate'
import { AnalyticsActionType } from 'types/common/analytics.type'
import { EVENTS, trackAnalytics } from 'utils/analytics'

import styles from './Explore.module.scss'

const Explore = () => {
  const hardNavigate = useHardNavigate()

  const keyTasksRef = useRef<HTMLDivElement>(null)
  const guidanceForExploreRef = useRef<HTMLDivElement>(null)
  const guidanceForOneBriefRef = useRef<HTMLDivElement>(null)
  const resourcesRef = useRef<HTMLDivElement>(null)

  return (
    <div className={styles.Explore}>
      <TopReturnToLandingButton />

      <PageBanner
        heading="Explore"
        description="Define the powerful insights that will propel our creative direction. Focus on audience insights, E2E planning, and how the product and brand address problems."
        showBubble
        bubbleText="Get the likes & wonders rolling"
        backgroundStyle={{ background: 'linear-gradient(180deg, #FFBB9E 0%, #FF8E5F 100%)' }}
        showNavigationCircle
        bigTextFillColor="#FF8E5F"
        page={FrameworkWheelPage.Explore}
      />
      <NavBar
        items={[
          { key: 'key-tasks', title: 'Key Tasks', ref: keyTasksRef },
          { key: 'guidance-for-explore', title: 'Guidance for Explore', ref: guidanceForExploreRef },
          { key: 'guidance-for-one-brief', title: 'Guidance for one brief', ref: guidanceForOneBriefRef },
          { key: 'resources', title: 'Resources', ref: resourcesRef },
        ]}
      />
      <KeyTasks
        ref={keyTasksRef}
        tasks={[
          {
            title: 'Immerse in learnings',
            description:
              'Look at audience insights, BVA, Persistent Platform, including the product and consumer truths, to understand the role of product and brand. Start with audience planning, which will inform E2E planning.',
            backgroundColor: '#FFEAE0',
          },
          {
            title: 'Conduct additional exploration',
            description:
              'Collaborate with Human Insights, E2E, Network, Agency Partners, Studio X, Social, Digital, C&CL, and others.',
            backgroundColor: '#FFEAE0',
          },
          {
            title: 'Write One Brief',
            description: 'Get feedback from OUs, markets, and key capabilities.',
            backgroundColor: '#FFEAE0',
          },
          {
            title: 'Develop E2E architecture strategy',
            description:
              'Make use of audience insights. Include final audience hierarchy and potential partnerships to explore.',
            backgroundColor: '#FFEAE0',
          },
          {
            title: 'Review and sign-off on brief',
            description: 'Allow time for sponsor review and any iterations.',
            backgroundColor: '#FFEAE0',
            footer: {
              footerTitle: 'Conduct retrospective',
              actions: [
                {
                  text: 'Mural',
                  link: 'https://app.mural.co/t/marketing6265/m/marketing6265/1747976237864/f1cf546811959a5c0c18fc38ec66fbdef1cdaf45',
                  icon: <LinkIcon />,
                },
                {
                  text: 'Video',
                  link: 'https://facade-api-tccc-prod-coke.playground.coke.com/api/file-manager/v1/tenants/571f9aa5-bb8b-4f9f-8835-23e74b64e050/files/2a030acb-1e71-4c49-9a93-ba498e592865/public-download',
                  icon: <VideoIcon />,
                },
              ],
            },
          },
        ]}
        inputs={[
          { text: 'Project Blueprint' },
          { text: 'Past Learnings' },
          { text: 'Audience Hierarchy (Growth & Design Audience)' },
          { text: 'BVA' },
          {
            text: 'Persistent Platform and within platform the product role and lense to use',
            link: process.env.REACT_APP_ENVIRONMENT_PERSISTENT_PLATFORM_URL,
          },
          { text: 'Network / Market/ Capabilities Input' },
          { text: 'Human Insights & Media Tools & Capabilities (i.e. UMM)' },
        ]}
        outputs={[
          {
            text: 'One Brief',
            link: 'https://facade-api-tccc-prod-coke.playground.coke.com/api/file-manager/v1/tenants/571f9aa5-bb8b-4f9f-8835-23e74b64e050/files/29e803c6-c1a4-43df-8ccb-f7c57d0da348/public-download',
          },
          { text: 'E2E Strategy including final audience hierarchy' },
          {
            text: 'Reflections & Improvements on Ways of Working',
            link: 'https://app.mural.co/t/marketing6265/m/marketing6265/1747976237864/f1cf546811959a5c0c18fc38ec66fbdef1cdaf45',
          },
        ]}
      />
      <div className="w-full bg-[#F4F4F4]">
        <GridCardLayout
          ref={guidanceForExploreRef}
          title="Guidance for Explore"
          titleDecoration="stars"
          cards={[
            {
              title: 'Not all cases need equal exploring',
              description: 'For existing BBEIs you need a sense-check.',
            },
            {
              title: 'Human Insights will play a key role',
              description: 'Make sure we are defining the right tensions and focused on human-centricity.',
            },
            {
              title: 'Keep the team lean',
              description:
                'Three to five people from TCCC + WPP at full dedication can make this work better and faster. Have one author write the final story.',
            },
            {
              title: 'Ask for feedback',
              description:
                "Allow time to convene with sponsors and any relevant capabilities and OUs—but don't feel obliged to incorporate all of it.",
            },
            {
              title: 'Your brief is a living organism',
              description:
                'At this stage, 90% of the information should be there, but the brief should also be able to adapt as the process progresses.',
            },
            {
              title: 'Always start from audience planning',
              description: "Don't jump into creative ideation without thorough audience planning.",
            },
          ]}
        />

        <div className={clsx(mainStyles.container, styles.stepsContainer)}>
          <div className={styles.title}>Steps</div>
          <div className={styles.stepsLayout}>
            <div className={styles.card}>
              <div className={styles.cardTitle}>Immersion info persistent elements</div>
              <div className={styles.cardDescription}>
                Work closely with E2E audience planning and Human Insights to guide, challenge, and support you with
                insight tools.
              </div>
              <div className={styles.cardNumber}>1</div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardTitle}>Integrate Assemble learnings</div>
              <div className={styles.cardDescription}>Determine what needs to be updated or changed.</div>
              <div className={styles.cardNumber}>2</div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardTitle}>Write the brief</div>
              <div className={styles.cardDescription}>Use one author, with input from core team.</div>
              <div className={styles.cardNumber}>3</div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardTitle}>SME Feedback</div>
              <div className={styles.cardDescription}>Collect feedback from OUs / markets and key capabilities.</div>
              <div className={styles.cardNumber}>4</div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardTitle}>Sponsor feedback</div>
              <div className={styles.cardDescription}>Allow this feedback to guide the discussion objectively.</div>
              <div className={styles.cardNumber}>5</div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardTitle}>Iterations and sign-off</div>
              <div className={styles.cardDescription}>
                Budget additional time for fine-tunings and final sponsor sign-off.
              </div>
              <div className={styles.cardNumber}>6</div>
            </div>
          </div>
        </div>
      </div>

      <GridCardLayout
        ref={guidanceForOneBriefRef}
        title="Guiding Principles for One Brief"
        titleDecoration="stars"
        className="!pt-[120px]"
        cards={[
          {
            title: 'Great briefs are SIMPLE, yet inspiring.',
            description: "“If you can't explain it simply, you don't understand it well enough.”",
            cardClassName: '!bg-[#FAFAFA]',
          },
          {
            title: 'Great briefs are WELL-CRAFTED',
            description:
              'The brief is not a form to fill out. Instead, it should feel like a continuous, well-authored story. Each section should flow into the next, creating a cohesive narrative.',
            cardClassName: '!bg-[#FAFAFA]',
          },
          {
            title: 'Great briefs are TIGHT',
            description:
              'A tight brief avoids leaving too much room for more decisions to be made further down the road. We often fear that leaving things out will come at the expense of getting to great work, but the opposite is true.',
            cardClassName: '!bg-[#FAFAFA]',
          },
          {
            title: 'Great briefs SOLVE problems',
            description:
              'Great ideas come from great problems. The brief should start by identifying a clear business problem to solve.',
            cardClassName: '!bg-[#FAFAFA]',
          },
          {
            title: 'And remember, there can only be ONE brief',
            description:
              'There are too many “briefs”, which leads to a lack of clarity on the problem to solve, alignment on ONE brief ensures that everyone involved in the project is on the same page, it creates a single source of truth to judge the work .',
            cardClassName: 'col-span-2 !bg-[#FAFAFA]',
          },
        ]}
      />

      <div className="w-full bg-[#F4F4F4]">
        <div className={clsx(styles.exploreResources, mainStyles.container)} ref={resourcesRef}>
          <div className={styles.title}>
            <span>Explore your resources</span>
          </div>

          <div className={styles.grid}>
            <div className={styles.content}>
              <div>
                <div className={styles.contentTitle}>Supporting Materials and Resources: AI Skill Pills</div>
                <div
                  className={styles.contentRow}
                  onClick={() =>
                    window.open(
                      `${process.env.REACT_APP_ENVIRONMENT_ONBOARDING_URL}?tab=3&phase=lens%20toolkit`,
                      '_blank',
                    )
                  }
                >
                  <div className={styles.contentIcon}>
                    <LinkIcon />
                  </div>
                  <div className={styles.content}>
                    <p className={styles.rowTitle}>Explore product-centric lenses</p>
                    <p className={styles.rowDescription}>
                      Aim to define and inspire how the product should be communicated in the Experience Loop.
                    </p>
                  </div>
                  <div className={styles.rowArrow}>
                    <ArrowIcon />
                  </div>
                </div>

                <div
                  className={styles.contentRow}
                  onClick={() =>
                    window.open(
                      `${process.env.REACT_APP_ENVIRONMENT_ONBOARDING_URL}?tab=3&phase=other%20materials`,
                      '_blank',
                    )
                  }
                >
                  <div className={styles.contentIcon}>
                    <LinkIcon />
                  </div>
                  <div className={styles.content}>
                    <p className={styles.rowTitle}>Explore trialing Inspiration</p>
                    <p className={styles.rowDescription}>
                      If your goal is to drive trial, do an in-depth analysis of various trialing activations and best
                      practices to inform the approach.
                    </p>
                  </div>
                  <div className={styles.rowArrow}>
                    <ArrowIcon />
                  </div>
                </div>

                <div
                  className={styles.contentRow}
                  onClick={() =>
                    window.open(
                      'https://facade-api-tccc-prod-coke.playground.coke.com/api/file-manager/v1/tenants/571f9aa5-bb8b-4f9f-8835-23e74b64e050/files/6cd57599-b9bd-42f8-96f5-3382f37aa0b3/public-download',
                      'blank_',
                    )
                  }
                >
                  <div className={styles.contentIcon}>
                    <DocumentIcon />
                  </div>
                  <div className={styles.content}>
                    <p className={styles.rowTitle}>Get closer your audience</p>
                    <p className={styles.rowDescription}>
                      Use this prompt to better understand your design audience for your One Brief
                    </p>
                  </div>
                  <div className={styles.rowArrow}>
                    <ArrowIcon />
                  </div>
                </div>

                <div
                  className={styles.contentRow}
                  onClick={() =>
                    window.open(
                      'https://facade-api-tccc-prod-coke.playground.coke.com/api/file-manager/v1/tenants/571f9aa5-bb8b-4f9f-8835-23e74b64e050/files/8473cfd7-7fa7-4e02-a513-79a97400a818/public-download',
                      'blank_',
                    )
                  }
                >
                  <div className={styles.contentIcon}>
                    <DocumentIcon />
                  </div>
                  <div className={styles.content}>
                    <p className={styles.rowTitle}>Refine the business problem</p>
                    <p className={styles.rowDescription}>
                      Use this prompt to refine the business problem for your One Brief
                    </p>
                  </div>
                  <div className={styles.rowArrow}>
                    <ArrowIcon />
                  </div>
                </div>

                <div
                  className={styles.contentRow}
                  onClick={() =>
                    window.open(
                      'https://facade-api-tccc-prod-coke.playground.coke.com/api/file-manager/v1/tenants/571f9aa5-bb8b-4f9f-8835-23e74b64e050/files/ad9ea493-2d36-437d-93e1-883ca8e946ee/public-download',
                      'blank_',
                    )
                  }
                >
                  <div className={styles.contentIcon}>
                    <DocumentIcon />
                  </div>
                  <div className={styles.content}>
                    <p className={styles.rowTitle}>Refine the consumer opportunity</p>
                    <p className={styles.rowDescription}>
                      Use this prompt to refine the consumer opportunity for your One Brief
                    </p>
                  </div>
                  <div className={styles.rowArrow}>
                    <ArrowIcon />
                  </div>
                </div>

                <div
                  className={styles.contentRow}
                  onClick={() =>
                    window.open(
                      'https://facade-api-tccc-prod-coke.playground.coke.com/api/file-manager/v1/tenants/571f9aa5-bb8b-4f9f-8835-23e74b64e050/files/31ebe8e0-61b5-4e15-ad8b-745a87c09ed6/public-download',
                      'blank_',
                    )
                  }
                >
                  <div className={styles.contentIcon}>
                    <DocumentIcon />
                  </div>
                  <div className={styles.content}>
                    <p className={styles.rowTitle}>Build your Truth-Need-Tension</p>
                    <p className={styles.rowDescription}>
                      Review suggested prompts to define key insights leveraging the TNT framework
                    </p>
                  </div>
                  <div className={styles.rowArrow}>
                    <ArrowIcon />
                  </div>
                </div>

                <div
                  className={styles.contentRow}
                  onClick={() =>
                    window.open(
                      'https://facade-api-tccc-prod-coke.playground.coke.com/api/file-manager/v1/tenants/571f9aa5-bb8b-4f9f-8835-23e74b64e050/files/8601ca15-55c0-4628-8396-78fcab3d08f7/public-download',
                      'blank_',
                    )
                  }
                >
                  <div className={styles.contentIcon}>
                    <VideoIcon />
                  </div>
                  <div className={styles.content}>
                    <p className={styles.rowTitle}>Design thinking</p>
                    <p className={styles.rowDescription}>
                      Understand how to design and build a user journey as part of your E2E experience architecture
                      design.{' '}
                    </p>
                  </div>
                  <div className={styles.rowArrow}>
                    <ArrowIcon />
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.content}>
              <div className={styles.contentTitle}>Quick links</div>

              <div
                className={styles.contentRow}
                onClick={() =>
                  window.open(
                    'https://facade-api-tccc-prod-coke.playground.coke.com/api/file-manager/v1/tenants/571f9aa5-bb8b-4f9f-8835-23e74b64e050/files/29e803c6-c1a4-43df-8ccb-f7c57d0da348/public-download',
                    'blank',
                  )
                }
              >
                <div className={styles.contentIcon}>
                  <DocumentIcon />
                </div>
                <div className={styles.content}>
                  <div className={styles.rowTitle}>One Brief</div>
                </div>
              </div>

              <div
                className={styles.contentRow}
                onClick={() => window.open(`${process.env.REACT_APP_ENVIRONMENT_PERSISTENT_PLATFORM_URL}`, '_blank')}
              >
                <div className={styles.contentIcon}>
                  <LinkIcon />
                </div>
                <div className={styles.content}>
                  <div className={styles.rowTitle}>Persistent Platform</div>
                </div>
              </div>

              <div
                className={styles.contentRow}
                onClick={() =>
                  window.open(
                    'https://form.jotform.com/250301711276345?muralUrl=https://app.mural.co/t/marketing6265/m/marketing6265/1747915560499/20a057859860d37b943527be9592d4b3283fc8de',
                    'blank',
                  )
                }
              >
                <div className={styles.contentIcon}>
                  <LinkIcon />
                </div>
                <div className={styles.content}>
                  <div className={styles.rowTitle}>Define the Lenses</div>
                </div>
              </div>

              <div
                className={styles.contentRow}
                onClick={() =>
                  window.open(
                    'https://groups.coca-cola.com/:f:/s/TransformersNetwork/Ev48zEpNOy5OhI7SYJN9xeIB0W6AI2L11vkHMmWqyUdbPA?e=b07hO7',
                    'blank_',
                  )
                }
              >
                <div className={styles.contentIcon}>
                  <FolderIcon />
                </div>
                <div className={styles.content}>
                  <div className={styles.rowTitle}>AI Skill Pills</div>
                </div>
              </div>

              <div
                className={styles.contentRow}
                onClick={() =>
                  window.open(
                    'https://app.mural.co/t/marketing6265/m/marketing6265/1747976237864/f1cf546811959a5c0c18fc38ec66fbdef1cdaf45',
                    'blank_',
                  )
                }
              >
                <div className={styles.contentIcon}>
                  <LinkIcon />
                </div>
                <div className={styles.content}>
                  <div className={styles.rowTitle}>Team Retrospective</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={clsx(mainStyles.container)}>
          <div className="flex justify-center">
            <Button
              variant="navigationControlBlack"
              text="Go to Assemble"
              iconLeft={<ChevronRight style={{ transform: 'rotate(180deg)' }} />}
              border
              className="mr-4"
              onClick={() => {
                hardNavigate('page-assemble')
                trackAnalytics({
                  type: AnalyticsActionType.action,
                  payload: EVENTS.ACTION.EXPLORE.TO_ASSEMBLE,
                })
              }}
            />
            <ReturnToLandingButton variant="navigationControlHoverWhite" />
            <Button
              variant="navigationControlBlack"
              text="Go to Create"
              iconRight={<ChevronRight />}
              border
              className="ml-4"
              onClick={() => {
                hardNavigate('page-create')
                trackAnalytics({
                  type: AnalyticsActionType.action,
                  payload: EVENTS.ACTION.EXPLORE.TO_CREATE,
                })
              }}
            />
          </div>
        </div>
      </div>

      <BottomArc backgroundColor="#F4F4F4" />
    </div>
  )
}

export default Explore
